/**
 * AG Grid server-side datasource for the Documents tab (DV-330 / DV-248),
 * modeled on fund-admin-frontend's `TicketsGraphqlDataSource`.
 *
 * Impedance mismatch handled here: AG Grid's server-side row model requests
 * blocks by absolute row range (`startRow`/`endRow`), but `vaultDocumentsSearch`
 * is FORWARD-CURSOR ONLY (`first`/`after` + `endCursor`) with no random access.
 * We therefore map block index -> cursor: block 0 uses `after: null`, and after
 * each fetch we remember the returned `endCursor` as the cursor for the next
 * block. Sequential scroll/paging is one fetch per block. A jump to (or a page
 * deep link / reload on) a block whose cursor we haven't discovered yet is served
 * by walking the cursor forward from the deepest known block — the intervening
 * blocks are fetched only to reveal their cursors (see `serveBlock`). Any change
 * to filters/sort purges the cursor map and restarts.
 *
 * The QUERY CONTEXT (`spaceIds`, `domain`, `validationStatus`, `attributeColumns`)
 * is fixed for the life of an instance — there are no setters. A scope or health-tab
 * change is expressed by constructing a NEW datasource, which starts with an empty
 * cursor map by definition; `DocumentBrowserContainer` does that in a `useMemo` and
 * re-keys the grid so the row model re-requests block 0. Nothing here needs to
 * detect it, which is why only the grid's own request feeds `modelKey`.
 */
import { type ApolloClient } from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { VaultDocumentsSearchDocument } from "#graphql/supergraphHooks";
import {
  type VaultDocumentsInput,
  type VaultDocumentsSearchQuery,
  type VaultDocumentsSearchQueryVariables,
  type VaultDocumentValidationStatus,
} from "#graphql/supergraphTypes";
import { abandonRequest } from "../serverSideDatasource";
import { type AttributeColumnMeta, gridModelToVaultDocumentsInput } from "./mappers";
import type { VaultDocumentNode } from "./hooks/useVaultDocumentsSearch";

/** The `vaultDocumentsSearch` connection (nodes + forward-cursor page info). */
type VaultDocumentsConnection = VaultDocumentsSearchQuery["vaultDocumentsSearch"];

export interface VaultDocumentsDataSourceProps<TRow> {
  /** Map a server node to a grid row. */
  mapNode: (node: VaultDocumentNode) => TRow;
  /** Block size, kept in sync with the grid's `cacheBlockSize` -> `first`. */
  pageSize: number;
  /** Topbar space filter -> `spaceIds`. */
  spaceIds?: string[] | null;
  /** Topbar domain filter -> `domain` slug strings. */
  domain?: string[] | null;
  /**
   * Health-tab filter -> `validationStatus` (Filed = `["validated"]`, awaiting
   * filing = `["quarantined"]`, All = null/omitted).
   */
  validationStatus?: VaultDocumentValidationStatus[] | null;
  /** AG Grid attribute-column id -> backend attribute descriptor. */
  attributeColumns?: Record<string, AttributeColumnMeta>;
  /** Called after each successful fetch with the connection's `totalCount`. */
  onTotalCountChange?: (totalCount: number) => void;
}

export class VaultDocumentsGraphqlDataSource<TRow> implements AgGrid.IServerSideDatasource {
  private client: ApolloClient<object>;
  private mapNode: (node: VaultDocumentNode) => TRow;
  private pageSize: number;
  private spaceIds?: string[] | null;
  private domain?: string[] | null;
  private validationStatus?: VaultDocumentValidationStatus[] | null;
  private attributeColumns: Record<string, AttributeColumnMeta>;
  private onTotalCountChange?: (totalCount: number) => void;

  /** Block index -> the `after` cursor that fetches it (block 0 = null). */
  private blockCursors = new Map<number, string | null>();
  /** Serialized filter+sort of the last request, to detect resets. */
  private lastModelKey: string | null = null;
  /**
   * Bumped ONLY when the filter/sort/space/domain model changes. In-flight
   * responses from an older generation are dropped — after a model change the
   * grid re-requests from block 0, so those params are abandoned. Crucially,
   * concurrent blocks within the SAME generation are not dropped (AG Grid SSRM
   * can fetch several blocks at once on fast scroll / cache refill), so a valid
   * earlier block resolving late still calls `success()`.
   */
  private generation = 0;

  constructor(client: ApolloClient<object>, props: VaultDocumentsDataSourceProps<TRow>) {
    this.client = client;
    this.mapNode = props.mapNode;
    this.pageSize = props.pageSize;
    this.spaceIds = props.spaceIds;
    this.domain = props.domain;
    this.validationStatus = props.validationStatus;
    this.attributeColumns = props.attributeColumns ?? {};
    this.onTotalCountChange = props.onTotalCountChange;
    this.blockCursors.set(0, null);
  }

  private blockIndex(startRow: number): number {
    return Math.floor(startRow / this.pageSize);
  }

  private resetCursors(): void {
    this.blockCursors.clear();
    this.blockCursors.set(0, null);
  }

  /**
   * Force the next `getRows` to refetch from block 0 with fresh cursors. Unlike
   * a filter/sort change, the model key is unchanged, so `getRows` won't reset on
   * its own — an external event (e.g. an upload completing) must call this. The
   * generation bump drops any in-flight block from before the refresh. Pair it
   * with the grid's `refreshServerSide({ purge: true })` so the row model purges
   * its cached blocks and re-requests.
   */
  refresh(): void {
    this.resetCursors();
    this.lastModelKey = null;
    this.generation += 1;
  }

  getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const startRow = params.request.startRow ?? 0;
    const blockIndex = this.blockIndex(startRow);

    // Filters/sort come from the grid request; space/domain from the topbar.
    const input = gridModelToVaultDocumentsInput(
      params.request.filterModel,
      params.request.sortModel,
      {
        spaceIds: this.spaceIds,
        domain: this.domain,
        validationStatus: this.validationStatus,
        first: this.pageSize,
        attributeColumns: this.attributeColumns,
      },
    );

    // A change in filters/sort invalidates every discovered cursor. ONLY the grid
    // request belongs in this key: `spaceIds` / `domain` / `validationStatus` are
    // constructor-set and never mutated, so they are identical on every call and
    // could never make the key differ. Their invalidation comes from the owner
    // throwing this datasource away and building a new one — see the class docs.
    const modelKey = JSON.stringify({
      filterModel: params.request.filterModel,
      sortModel: params.request.sortModel,
    });
    if (modelKey !== this.lastModelKey) {
      this.resetCursors();
      this.lastModelKey = modelKey;
      this.generation += 1;
    }

    void this.serveBlock(blockIndex, input, this.generation, params);
  }

  /**
   * Fetch one cursor page. Returns the connection, `null` on a missing/failed
   * response, or `undefined` when a newer generation has superseded this request
   * (a model change re-requested from block 0, so the result is abandoned).
   */
  private async fetchBlock(
    input: VaultDocumentsInput,
    after: string | null,
    generation: number,
  ): Promise<VaultDocumentsConnection | null | undefined> {
    const response = await this.client.query<
      VaultDocumentsSearchQuery,
      VaultDocumentsSearchQueryVariables
    >({
      query: VaultDocumentsSearchDocument,
      variables: { input: after != null ? { ...input, after } : input },
      fetchPolicy: "no-cache",
    });
    if (generation !== this.generation) return undefined;
    return response.data?.vaultDocumentsSearch ?? null;
  }

  /**
   * Serve a block by absolute index. The backend is forward-cursor only, so a
   * block whose `after` cursor hasn't been discovered yet — a jump to page N, or
   * a deep link / reload landing directly on page N — is reached by walking
   * forward from the deepest known cursor, fetching the intervening blocks purely
   * to discover their `endCursor`s. Sequential next/prev stays a single fetch,
   * since the previous block already discovered the next cursor.
   */
  private async serveBlock(
    blockIndex: number,
    input: VaultDocumentsInput,
    generation: number,
    params: AgGrid.IServerSideGetRowsParams,
  ): Promise<void> {
    try {
      // Discover cursors forward until the target block's `after` is known.
      while (this.blockCursors.get(blockIndex) === undefined) {
        // Block 0's cursor (null) is always known, so this resolves to the
        // deepest known block at or below the target.
        let probe = blockIndex - 1;
        while (probe > 0 && this.blockCursors.get(probe) === undefined) probe -= 1;

        const conn = await this.fetchBlock(
          input,
          this.blockCursors.get(probe) ?? null,
          generation,
        );
        if (conn === undefined) return void abandonRequest(params); // superseded
        if (!conn) return void params.fail();

        if (conn.pageInfo.hasNextPage && conn.pageInfo.endCursor != null) {
          this.blockCursors.set(probe + 1, conn.pageInfo.endCursor);
        } else {
          // Ran out of pages before reaching the target block.
          return void params.fail();
        }
      }

      const conn = await this.fetchBlock(
        input,
        this.blockCursors.get(blockIndex) ?? null,
        generation,
      );
      if (conn === undefined) return void abandonRequest(params); // superseded
      if (!conn) return void params.fail();

      const { nodes, pageInfo, totalCount } = conn;
      this.onTotalCountChange?.(totalCount);

      // Remember the cursor for the next block so sequential paging is one fetch.
      if (pageInfo.hasNextPage && pageInfo.endCursor != null) {
        this.blockCursors.set(blockIndex + 1, pageInfo.endCursor);
      }

      const rowData = nodes.map((n) => this.mapNode(n));
      // `totalCount` gives an exact last-row index (no over-fetch needed).
      params.success({ rowData, rowCount: totalCount });

      if (params.api) {
        if (totalCount === 0) params.api.showNoRowsOverlay();
        else params.api.hideOverlay();
      }
    } catch {
      params.fail();
    }
  }
}
